import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronUp, FileText } from 'lucide-react';
import { apiService } from '@/lib/api';
import { usePermissions } from '@/hooks/usePermissions';
import { ESTIMATE_STATUS_COLORS } from '@/lib/estimateStatus';
import type { EstimateSearchDTO } from '@/types';
import { CollapsibleSummaryHeader } from './ui/CollapsibleSummaryHeader';

interface Props {
  /** Exactly one of contact_id / source_job_id. */
  filter: Pick<EstimateSearchDTO, 'contact_id' | 'source_job_id'>;
  companyId?: string;
  onOpen: (estimateId: string) => void;
  /** Rendered when there are none; omit to render nothing at all. */
  emptyText?: string;
  /**
   * 'card' (default): plain card, always open (Contacts page).
   * 'desktop' / 'mobile': collapsible, starts collapsed, matching that job details view's cards
   * (JobExpensesCard on desktop, the Expenses section on mobile).
   */
  variant?: 'card' | 'desktop' | 'mobile';
}

const formatTotal = (n: number) => n.toLocaleString(undefined, { style: 'currency', currency: 'USD' });

/** Estimates of one contact, or created from one job. Hidden without the estimates feature. */
export default function LinkedEstimates({ filter, companyId, onOpen, emptyText, variant = 'card' }: Props) {
  const { isFeatureAllowed } = usePermissions();
  const enabled = isFeatureAllowed('estimates') && !!(filter.contact_id || filter.source_job_id);
  const [isExpanded, setIsExpanded] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: filter.source_job_id ? ['job-estimates', filter.source_job_id] : ['contact-estimates', filter.contact_id],
    queryFn: () => apiService.searchEstimates({ ...filter, sort_by: 'date', sort_direction: 'desc', page: 1, page_size: 100 }, companyId),
    enabled,
    staleTime: 30_000,
  });

  const estimates = data?.estimates ?? [];
  if (!enabled || (!emptyText && estimates.length === 0)) return null;

  const list = isLoading ? (
    <p className="py-4 text-sm text-gray-500">Loading estimates…</p>
  ) : estimates.length === 0 ? (
    <p className="py-4 text-sm text-gray-500">{emptyText}</p>
  ) : (
    <div className="space-y-2">
      {estimates.map((e) => {
        const colors = ESTIMATE_STATUS_COLORS[e.status] ?? ESTIMATE_STATUS_COLORS.Draft;
        return (
          <button
            key={e.id}
            type="button"
            className="flex w-full items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white p-3 text-left transition-colors hover:border-blue-300 hover:bg-gray-50"
            onClick={() => onOpen(e.id)}
          >
            <div>
              <p className="text-sm font-medium text-gray-900">Estimate #{e.estimate_number}</p>
              <p className="text-xs text-gray-500">
                {new Date(e.date).toLocaleDateString()}
                {e.address ? ` · ${e.address}` : ''}
              </p>
            </div>
            <div className="text-right">
              <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${colors.bg} ${colors.text}`}>{e.status}</span>
              <p className="mt-1 text-sm font-semibold text-gray-900">{formatTotal(e.total)}</p>
            </div>
          </button>
        );
      })}
    </div>
  );

  if (variant === 'card') {
    return (
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <h3 className="text-sm font-semibold text-gray-900">Estimates</h3>
        <div className="mt-3">{list}</div>
      </div>
    );
  }

  if (variant === 'mobile') {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="w-full px-2.5 py-2.5 flex items-center justify-between gap-2 bg-gray-50/50 hover:bg-gray-100/50 transition-colors"
        >
          <CollapsibleSummaryHeader
            leading={
              <div className="flex items-center gap-1.5 shrink-0">
                <FileText className="h-4 w-4 text-amber-500 shrink-0" />
                <span className="font-semibold text-gray-900">Estimates</span>
              </div>
            }
            trailing={<span className="font-semibold text-gray-900 whitespace-nowrap">{estimates.length}</span>}
          />
          <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
        {isExpanded && (
          <div className="border-t border-gray-100 p-3 animate-in slide-in-from-top-1">{list}</div>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-gray-50 rounded-lg ${isExpanded ? 'p-4' : 'p-3'} transition-all duration-200`}>
      <div
        className={`flex items-center justify-between cursor-pointer ${isExpanded ? 'mb-3' : ''}`}
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-4">
          <h3 className="text-m font-semibold text-gray-900 flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-500" />
            Estimates
          </h3>
          {!isExpanded && (
            <div className="flex items-center gap-3 text-sm">
              <div className="w-px h-5 bg-gray-300 mx-2"></div>
              <span className="text-gray-600 font-semibold">{estimates.length}</span>
            </div>
          )}
        </div>
        {isExpanded ? <ChevronUp className="h-5 w-5 text-gray-500" /> : <ChevronDown className="h-5 w-5 text-gray-500" />}
      </div>
      {isExpanded && list}
    </div>
  );
}
